import { type RowData } from "@tanstack/react-table";
import { type AppTable } from "../../../components/table/tableFeatures";
import { FaChevronDown, FaFloppyDisk } from "react-icons/fa6";
import { useTranslation } from "react-i18next";
import { css, cx } from "../../../../styled-system/css";
import {
  MenuButton,
  Menu,
  MenuItem,
  MenuSection,
  MenuSectionHeader,
  Separator,
} from "../../../components/core/basic/Menu";
import { useDataView } from "../hooks/useDataView";
import { Flex } from "../../../../styled-system/jsx";
import { SearchInput } from "../../../components/fields/base/TextField";
import { useMemo, useState } from "react";

type DataViewPickerProps<TData extends RowData> = {
  viewKey: string;
  datasetKey: string;
  table: AppTable<TData>;
};

const chipStyles = css.raw({
  display: "inline-flex",
  alignItems: "center",
  gap: "xsmall",
  backgroundColor: "gray.50",
  border: "1px solid {colors.border.default}",
  color: "text.secondary",
  paddingX: "large",
  paddingY: "small",
  fontWeight: "bolder",
  lineHeight: 1,
  whiteSpace: "nowrap",
  borderRadius: "pill",
});

export function DataViewPicker<TData extends RowData>({ viewKey, datasetKey, table }: DataViewPickerProps<TData>) {
  const { t } = useTranslation(["dataViews"]);
  const [searchFilter, setSearchFilter] = useState("");

  const {
    myViews,
    sharedViews,
    activeView,
    isDirty,
    isLoading,
    apply,
    revert,
    unapply,
    saveAsNew,
    updateCurrent,
    openManage,
    canManageView,
  } = useDataView({ viewKey, datasetKey, table });

  const filteredDataViews = useMemo(() => {
    const combinedDataViews = [...myViews, ...sharedViews];

    if (searchFilter) {
      return combinedDataViews.filter((view) => view.name.toLowerCase().includes(searchFilter.toLowerCase()));
    }
    return combinedDataViews;
  }, [searchFilter, myViews, sharedViews]);

  const totalViewCount = myViews.length + sharedViews.length;
  const hasViews = totalViewCount > 0;
  const showSearch = totalViewCount > 5;
  const canUpdate = activeView != null && isDirty && canManageView(activeView);
  const canRevert = activeView != null && isDirty;
  const hasSearchQuery = searchFilter.length > 0;
  const noSearchMatches = hasSearchQuery && filteredDataViews.length === 0;

  const triggerLabel =
    activeView ?
      <span className={cx("filter-badge", css(chipStyles, { fontSize: "small" }))}>
        <FaFloppyDisk />
        <span>{activeView.name}</span>
        {isDirty && <span className={css({ fontStyle: "italic" })}>{t(($) => $.dataViews["· modified"])}</span>}
        <FaChevronDown className={css({ fontSize: "large", marginLeft: "small" })} />
      </span>
    : <Flex alignItems={"center"} paddingX={"xsmall"}>
        <FaFloppyDisk />
      </Flex>;

  return (
    <MenuButton
      buttonProps={{
        variant: "contentOnly",
        tooltip: activeView ? t(($) => $.dataViews["Manage saved views"]) : t(($) => $.dataViews["Saved views"]),
      }}
      popoverProps={{ placement: "bottom", shouldFlip: false }}
      label={triggerLabel}
    >
      {hasViews && <MenuSectionHeader>{t(($) => $.dataViews["Apply a view"])}</MenuSectionHeader>}
      {showSearch && (
        <SearchInput
          aria-label={t(($) => $.dataViews.Search)}
          css={{ padding: "medium" }}
          autoFocus
          value={searchFilter}
          onChange={setSearchFilter}
        />
      )}
      <Menu className={css({ minWidth: "240px", maxHeight: "50vh", overflowY: "auto" })}>
        {isLoading && !hasViews ?
          <MenuItem id="loading" isDisabled>
            {t(($) => $.dataViews["Loading…"])}
          </MenuItem>
        : noSearchMatches ?
          <MenuItem id="no-matches" isDisabled>
            <span className={css({ color: "text.secondary", fontStyle: "italic" })}>
              {t(($) => $.dataViews["No views match “{query}”"], { query: searchFilter })}
            </span>
          </MenuItem>
        : filteredDataViews.map((view) => {
            const isActive = activeView?.id === view.id;
            return (
              <MenuItem key={view.id} textValue={view.name} onAction={() => apply(view)}>
                <span
                  className={css({
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "xsmall",
                    fontWeight: isActive ? "600" : "400",
                  })}
                >
                  {view.name}
                </span>
              </MenuItem>
            );
          })
        }
        {hasViews && <Separator />}
        <MenuSection>
          <MenuSectionHeader>{t(($) => $.dataViews.Manage)}</MenuSectionHeader>
          {activeView != null && (
            <MenuItem id="unapply" onAction={() => unapply()}>
              {t(($) => $.dataViews["Switch to default"])}
            </MenuItem>
          )}
          {canUpdate && (
            <MenuItem id="update-current" onAction={updateCurrent}>
              {t(($) => $.dataViews["Update view"])}
            </MenuItem>
          )}
          <MenuItem
            id="save-as-new"
            onAction={() => {
              void saveAsNew();
            }}
          >
            {t(($) => $.dataViews["Save as new view"])}
          </MenuItem>
          {canRevert && (
            <MenuItem id="revert" onAction={() => revert()}>
              {t(($) => $.dataViews["Discard changes"])}
            </MenuItem>
          )}
          <MenuItem id="manage" onAction={() => openManage()}>
            {t(($) => $.dataViews["Manage views"])}
          </MenuItem>
        </MenuSection>
      </Menu>
    </MenuButton>
  );
}
